import React from "react";
import { DatePicker, Form, Input, InputNumber, Modal, Select, type FormInstance } from "antd";
import dayjs from "dayjs";

import { SalesMeetingSaleStatus, SalesMeetingStatus } from "@fitness-app/data-models/entities/SalesMeeting";

import { meetingStatusLabels, ORDER_OPTION_PREFIX, saleStatusOptions } from "../salesCrmConstants";
import { formatMoney, normalizeCycles } from "../salesCrmUtils";
import { type FeedbackForm } from "../salesCrmTypes";
import { type BundleOptions } from "../hooks/useBundleOptionsQuery";

// Shared by the select's onChange and the auto-prefill after adding a manual
// meeting, so both paths apply the same sale-status and order-field side effects.
export const applyBundleSelection = (
  form: FormInstance<FeedbackForm>,
  bundleOptions: BundleOptions | undefined,
  value: string | undefined,
): void => {
  form.setFieldValue("customerBundleId", value);

  const currentSaleStatus = form.getFieldValue("saleStatus") as SalesMeetingSaleStatus | undefined;

  if (value && (!currentSaleStatus || currentSaleStatus === SalesMeetingSaleStatus.Waiting)) {
    form.setFieldValue("saleStatus", SalesMeetingSaleStatus.Yes);
  }

  if (value && form.getFieldValue("status") === SalesMeetingStatus.NoShow) {
    form.setFieldValue("status", SalesMeetingStatus.Attended);
  }

  if (value?.startsWith(ORDER_OPTION_PREFIX)) {
    const order = (bundleOptions?.orders ?? []).find((row) => row.id === value.slice(ORDER_OPTION_PREFIX.length));

    form.setFieldValue("manualCycles", normalizeCycles(order?.cycles));
    form.setFieldValue("manualSaleDate", order?.createdAt ? dayjs(order.createdAt) : null);

    if (order?.amount != null) {
      form.setFieldValue("manualAmount", order.amount / 100);
      form.setFieldValue("manualTotalAmount", (order.cycles ? order.amount * order.cycles : order.amount) / 100);
    } else {
      form.setFieldValue("manualAmount", null);
      form.setFieldValue("manualTotalAmount", null);
    }
  }
};

type FeedbackModalProps = {
  open: boolean;
  form: FormInstance<FeedbackForm>;
  confirmLoading: boolean;
  bundleOptions: BundleOptions | undefined;
  bundleOptionsLoading: boolean;
  onSearch: (value: string) => void;
  onCancel: () => void;
  onFinish: (values: FeedbackForm) => void;
};

export const FeedbackModal = ({
  open,
  form,
  confirmLoading,
  bundleOptions,
  bundleOptionsLoading,
  onSearch,
  onCancel,
  onFinish,
}: FeedbackModalProps): React.ReactElement => {
  const selectedBundleValue = Form.useWatch("customerBundleId", form);
  const isOrderLinkSelected =
    typeof selectedBundleValue === "string" && selectedBundleValue.startsWith(ORDER_OPTION_PREFIX);
  const selectedSaleStatus = Form.useWatch("saleStatus", form);
  const isNotQualified = selectedSaleStatus === SalesMeetingSaleStatus.NotQualified;

  return (
    <Modal
      title="Feedback po rozmowie"
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={confirmLoading}
      width={760}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
          <Form.Item name="status" label="Status spotkania" rules={[{ required: true }]}>
            <Select
              options={Object.values(SalesMeetingStatus).map((value) => ({
                value,
                label: meetingStatusLabels[value],
              }))}
            />
          </Form.Item>
          <Form.Item name="saleStatus" label="Status sprzedaży">
            <Select allowClear options={saleStatusOptions} />
          </Form.Item>
          <Form.Item name="customerBundleId" label="Płatność w koszyku">
            <Select
              allowClear
              showSearch
              filterOption={false}
              loading={bundleOptionsLoading}
              onSearch={onSearch}
              onChange={(value: string | undefined) => applyBundleSelection(form, bundleOptions, value)}
              placeholder="Wyszukaj po mailu, nazwie lub order ID"
              notFoundContent={bundleOptionsLoading ? "Szukam..." : "Brak danych"}
              options={[
                {
                  label: "Pakiety",
                  options: (bundleOptions?.bundles ?? []).map((bundle) => ({
                    value: bundle.id,
                    label: `${bundle.clientEmail} · ${bundle.name} · płatność ${formatMoney(
                      bundle.amount,
                      bundle.currency,
                    )} · zakontraktowane ${formatMoney(bundle.totalAmount, bundle.currency)} · ${dayjs(
                      bundle.createdAt,
                    ).format("DD.MM.YYYY")}`,
                  })),
                },
                {
                  label: "Zamówienia bez pakietu (zapis utworzy pakiet)",
                  options: (bundleOptions?.orders ?? []).map((order) => ({
                    value: `${ORDER_OPTION_PREFIX}${order.id}`,
                    label: `${order.clientEmail} · ${order.productName ?? order.id} · ${
                      order.paymentType === "free" ? "płatność manualna/free" : "opłacone"
                    } · ${formatMoney(order.amount, order.currency)} · ${dayjs(order.createdAt).format(
                      "DD.MM.YYYY",
                    )}`,
                  })),
                },
              ]}
            />
          </Form.Item>
          {isOrderLinkSelected ? (
            <>
              <Form.Item
                name="manualAmount"
                label="Opłacona kwota (PLN)"
                rules={[{ required: true, message: "Podaj opłaconą kwotę" }]}
              >
                <InputNumber min={0} step={0.01} precision={2} style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                name="manualSaleDate"
                label="Data sprzedaży"
                tooltip="Decyduje, do którego miesiąca rozliczenia trafi sprzedaż — domyślnie data zamówienia"
                rules={[{ required: true, message: "Podaj datę sprzedaży" }]}
              >
                <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
              </Form.Item>
              <Form.Item
                name="manualCycles"
                label="Liczba rat"
                tooltip="Zostaw puste dla płatności jednorazowej; minimum 2 raty — od tego zależy klasyfikacja w rozliczeniach (raty vs jednorazowe)"
              >
                <InputNumber
                  min={2}
                  max={48}
                  precision={0}
                  style={{ width: "100%" }}
                  placeholder="np. 8"
                  onChange={(cycles) => {
                    const paidAmount = form.getFieldValue("manualAmount") as number | null;

                    if (typeof paidAmount !== "number") return;

                    // Cleared field = one-time payment, so the contract equals
                    // the single paid amount.
                    form.setFieldValue(
                      "manualTotalAmount",
                      typeof cycles === "number" ? Math.round(paidAmount * cycles * 100) / 100 : paidAmount,
                    );
                  }}
                />
              </Form.Item>
              <Form.Item
                name="manualTotalAmount"
                label="Kwota kontraktu (PLN)"
                tooltip="Pełna wartość planu — suma wszystkich rat"
                rules={[{ required: true, message: "Podaj kwotę kontraktu" }]}
              >
                <InputNumber min={0} step={0.01} precision={2} style={{ width: "100%" }} />
              </Form.Item>
            </>
          ) : null}
        </div>
        {isNotQualified ? (
          <Form.Item
            name="notQualifiedReason"
            // Unmounting drops the value, so leaving the status and coming back
            // cannot resurrect a reason written for an earlier choice.
            preserve={false}
            label="Dlaczego nie kwalifikuje się"
            tooltip="Opcjonalne, zostaje u nas — do Meta idzie sam status, nigdy ten opis"
          >
            <Input.TextArea rows={2} placeholder="np. inna grupa docelowa, brak budżetu, fejkowy kontakt" />
          </Form.Item>
        ) : null}
        <Form.Item name="notes" label="Notatki">
          <Input.TextArea rows={4} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
